// The live-preview panel embedded at the end of the template editor. Ties
// the other three pieces together: builds a draft from the template
// (draft.ts, mirroring what the server sends), resolves the template's
// artwork references to URLs via the asset delivery manifest when an asset
// client context exists (features/assets/asset-queries — null otherwise,
// same as everywhere else artwork is optional), and wires the
// size/value/stale controls (preview-controls.tsx) to the iframe
// (preview-frame.tsx).
//
// A template is the whole of a screen's look and copy, so previewing one
// here shows exactly what every screen using it will render — there is
// nothing left for a per-screen preview to add.
//
// The input is a DRAFT, not a saved template: the editor feeds this its live
// form values, so the iframe follows every keystroke and every picked image
// without anything reaching a live screen first. Asset ids resolve through
// the delivery manifest exactly as saved ones do — the manifest does not
// care whether the id has been persisted yet.
//
// Size is NOT panel-local state: the operator's resize belongs on the
// template like every other field, so the caller (template-editor.tsx) owns
// `width`/`height` on the form's `previewWidth`/`previewHeight` and this
// panel is fully controlled for size — one save path, through the one
// SaveBar, never a second one live only in this component.

import { Card } from "@pej/kore/components";
import { useQueries } from "@tanstack/react-query";
import { useState } from "react";
import type { Template, TemplateSection } from "@/api/types";
import { useAssetClientContext } from "@/features/assets/asset-client-context";
import { pickArtworkUrl } from "@/features/assets/asset-projections";
import { manifestQuery, useArtworkUrl, useFontUrl } from "@/features/assets/asset-queries";
import type { DraftTemplate } from "./draft";
import { buildDraft } from "./draft";
import { DEFAULT_VALUE_MINUTES, PreviewControls, type PreviewSize } from "./preview-controls";
import { PreviewFrame } from "./preview-frame";

/** What the panel renders: everything `buildDraft` reads, plus the
 *  background asset reference it resolves to a URL on the draft's behalf.
 *  Every image *section*'s asset resolves the same way, via `imageAssetIds`
 *  below — there is no other dedicated artwork slot left. */
export interface PreviewTemplate extends DraftTemplate {
    backgroundImageAssetId: Template["backgroundImageAssetId"];
    /** The draft's uploaded font, resolved to its file URL for the preview. */
    fontAssetId: Template["fontAssetId"];
}

export interface PreviewPanelProps {
    template?: PreviewTemplate;
    width: number;
    height: number;
    onSizeChange: (size: PreviewSize) => void;
}

function isImageSection(section: TemplateSection): section is Extract<TemplateSection, { type: "image" }> {
    return section.type === "image";
}

/** Every distinct asset id an image section references, flattened out of the
 *  template's own ordered list — a section carries the reference, never the
 *  other way round, so there is no other place to collect these from. */
function imageAssetIdsOf(sections: readonly TemplateSection[]): string[] {
    const ids = sections.filter(isImageSection).map((section) => section.assetId);
    return Array.from(new Set(ids.filter((id): id is string => id !== null)));
}

export function PreviewPanel({ template, width, height, onSizeChange }: PreviewPanelProps) {
    const [valueMinutes, setValueMinutes] = useState(DEFAULT_VALUE_MINUTES);
    const [stale, setStale] = useState(false);

    const assetCtx = useAssetClientContext();
    const backgroundImageUrl = useArtworkUrl(assetCtx, template?.backgroundImageAssetId ?? null);
    const fontFaceUrl = useFontUrl(assetCtx, template?.fontAssetId ?? null);

    // One manifest request per distinct image asset a section references,
    // sharing the same cache (and query key) `useArtworkUrl` and the picker
    // use — a manifest already fetched anywhere else in the editor costs
    // nothing to read again here.
    const imageAssetIds = imageAssetIdsOf(template?.sections ?? []);
    const manifests = useQueries({
        queries: imageAssetIds.map((assetId) => ({
            ...manifestQuery(assetCtx, assetId),
            enabled: assetCtx !== null,
            retry: false,
        })),
    });
    const sectionImageUrls = new Map<string, string>();
    imageAssetIds.forEach((assetId, index) => {
        const result = manifests[index];
        // Same guard as `useArtworkUrl`: an errored manifest (deleted asset)
        // must not resolve to a stale URL React Query kept around.
        if (!result || result.isError || !result.data) return;
        const url = pickArtworkUrl(result.data);
        if (url) sectionImageUrls.set(assetId, url);
    });

    const value = buildDraft({
        template,
        valueMinutes,
        stale,
        assetUrls: { backgroundImageUrl, sectionImageUrls, fontFaceUrl },
    });

    return (
        <Card className="p-6">
            <h2 className="mb-1 text-base font-semibold">Live preview</h2>
            <p className="mb-4 text-sm text-muted-foreground">
                Exactly what a screen using this template will show. Changes appear here as you make them; nothing
                reaches a live screen until you save.
            </p>
            <div className="flex flex-col gap-4">
                <PreviewControls
                    width={width}
                    height={height}
                    onSizeChange={onSizeChange}
                    valueMinutes={valueMinutes}
                    onValueMinutesChange={setValueMinutes}
                    stale={stale}
                    onStaleChange={setStale}
                />
                <PreviewFrame value={value} width={width} height={height} onSizeChange={onSizeChange} />
            </div>
        </Card>
    );
}
